import { z } from 'zod';
import {
  createModel,
  currencySchema,
  imageWithUploadSchema,
  QueryMany,
  QueryOne,
  refSchema,
} from '../../common';
import { dateSchema, nameSchema, priceSchema } from '../../validation';
import { TransactionTypeSchema } from './transaction.type.model';
import { transactionWalletSchema } from './transaction.wallet.model';
import { TransactionStatusSchema } from './transaction.status.model';
import { transactionInvoiceSchema } from './transaction.invoice.model';
import { transactionFlowSchema } from './transaction.flow.model';
import { paymentGatewaySchema } from '../../common';
import { UTagSharedSchema } from '../utag';
/** `Transaction db model` */
export const TransactionSchema = createModel({
  /**
   * - `pending` = the transaction has not yet been settled
   * - `waiting_for_approval` = this will only be available when the user is makes a wire transfer from his bank,
   * and our finance team needs to manually verify the transaction and change the status to `success`.
   * - `success` = the transaction is successfully settled.
   * - `failed` = something wrong with the transaction.
   */
  status: TransactionStatusSchema,

  /** the currency of the transaction */
  currency: currencySchema,
  /** transaction ref (main & short) */
  ref: refSchema,
  /** the total amount of the transaction. This amount cloud be positive or negative.
   *
   * - `positive` = the transaction will increment the associated wallet balance
   * - `negative` = the transaction will decrement the associated wallet balance
   */
  amount: priceSchema,
  /** How much this transaction is using in credit from the wallet.
   *
   * For example, a customer orders something for 1,200 SAR, and the wallet has a balance of `1,000 SAR` `BEFORE` the transaction.
   *
   * The `creditAmount` will be `200 SAR` because the customer will be using the existing `1,000 SAR` balance from the wallet to pay for the order.
   *
   * SO it will be like:
   * - `amount` = -1,200 SAR .... Negative transaction amount because deducting from wallet balance either directly or via credit.
   * - `debitAmount` = 1,000 SAR
   * - `creditAmount` = 200 SAR
   *
   */
  creditAmount: priceSchema,
  /** How much this transaction is using debit from the wallet (existing money / available balance).
   *
   * For example, a customer orders something for 1,200 SAR, and the wallet has a balance of `1,000 SAR` BEFORE the transaction.
   *
   * The `debitAmount` will be `1,000 SAR` because the customer will be using the existing `1,000 SAR` balance from the wallet to pay for the order.
   *
   * SO it will be like:
   * - `amount` = -1,200 SAR .... Negative transaction amount because deducting from wallet balance either directly or via credit.
   * - `debitAmount` = 1,000 SAR
   * - `creditAmount` = 200 SAR
   */
  debitAmount: priceSchema,
  /** the date of the transaction */
  createdAt: dateSchema,
  /**
   * - `carrier_capture_order_earnings` = capture the order earnings for the carrier `No ACTUAL PAYMENT YET`
   * - `carrier_payout` = pay the carrier out `PAYMENT IS MADE`
   *
   * - `customer_single_order_payment` = customer pays for a single order `PAYMENT IS MADE`
   * - `customer_invoice_payment` = customer pays for an invoice `PAYMENT IS MADE`
   * - `customer_pay_all_due_payments` = customer pays all due payments `PAYMENT IS MADE`
   * - `customer_capture_order_amount` = capture the order amount for the customer `No ACTUAL PAYMENT YET`
   * - `customer_advance_payment` = customer makes an advance payment to their wallet `PAYMENT IS MADE`
   *
   * - `wallet_debit` = debit the wallet `PAYMENT IS MADE`
   * - `wallet_credit` = credit the wallet `PAYMENT IS MADE`
   * - `refund` = refund the transaction `PAYMENT IS MADE`
   * - `cashback` = cashback reward transaction `PAYMENT IS MADE`
   */
  type: TransactionTypeSchema,
  /** the payment gateway of the transaction. Cash, Bank, Online Payment, etc.
   *
   * It's optional because there are some transaction types that does not `YET` have an actual amount transferred such as `carrier_earnings` or `customer_capture_order_amount`.
   *
   * If there is an actual money movement, then we `MUST` have a gateway.
   */
  gateway: paymentGatewaySchema.optional(),

  /** this will be any images or proof of documents related to the transaction */
  images: z.array(imageWithUploadSchema),

  /** any notes related to the transaction */
  notes: nameSchema.optional(),

  /**
   * the flow of money of the transaction on the wallet:
   *
   * - `-1` means the transaction amount is `subtracted` from the wallet `balance`
   * - `1` means the transaction amount is `add` to the wallet `balance`
   */
  flow: transactionFlowSchema,

  /** the wallet in which this transaction happens on */
  wallet: transactionWalletSchema,

  /**
   * the list of invoices that this transaction will settle.
   *
   * Mostly it will be one invoice.
   *
   * However, in some cases it could be multiple invoices such as when paying for more than one invoice in one transaction.
   */
  invoices: z.array(transactionInvoiceSchema).min(0),

  /** Date when the transaction was approved */
  approvedAt: dateSchema.optional(),
  /** Identifier of who approved the transaction */
  approvedBy: nameSchema.optional(),
  /** Reason for rejecting the transaction */
  rejectionReason: nameSchema.optional(),
  /** Date when the transaction was rejected */
  rejectedAt: dateSchema.optional(),
  /** Identifier of who rejected the transaction */
  rejectedBy: nameSchema.optional(),
  UTags: z.array(UTagSharedSchema),
});

/** `Transaction db model` */
export type Transaction = z.infer<typeof TransactionSchema.model>;
export type TransactionData = z.infer<typeof TransactionSchema.data>;
export type TransactionQueryOne = QueryOne<Transaction>;
export type TransactionQueryMany = QueryMany<Transaction>;
export type TransactionPartial = z.infer<typeof TransactionSchema.partial>;
